
// src/components/ClientsPage/ClientDetailPage.jsx

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../api/api";
import { useTranslation } from "react-i18next";
import Modal from "../UI/Modal";
import Button from "../UI/Button";
import ConfirmModal from "../UI/ConfirmModal";

const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

const API_URL = `${BASE_URL}/api/clients/`;
const BALANCE_API = `${BASE_URL}/api/client-balances/by-client/`;

const emptyForm = {
  name: "",
  commission_rate: "",
  comment: "",
  telegram_chat_id: "",

  // Официальная информация
  official_name: "",
  official_adress: "",
  tax_identification_number: "",
  registration_number: "",
  kpp: "",
  bank_account: "",
  bank_nane: "",
  correspondent_account: "",
  bik: "",
  phone: "",
  email: "",
  authorized_person: "",
};

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("ClientsPage");

  const [client, setClient] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  // ================= EDIT =================

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // ================= DELETE =================

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ================= LOAD CLIENT =================

  const loadClient = async () => {
    try {
      const res = await authFetch(`${API_URL}${id}/`);

      if (!res.ok) {
        throw new Error("Не удалось загрузить клиента");
      }

      const data = await res.json();

      setClient(data);
    } catch (err) {
      console.error("Ошибка загрузки клиента:", err);
      setClient(null);
    }
  };

  // ================= LOAD BALANCE =================

  const loadBalance = async () => {
    try {
      const res = await authFetch(`${BALANCE_API}${id}/`);

      if (!res.ok) {
        throw new Error("Не удалось загрузить баланс");
      }

      const data = await res.json();

      setBalance(data);
    } catch (err) {
      console.error("Ошибка загрузки баланса:", err);
      setBalance(null);
    }
  };

  // ================= LOAD =================

  useEffect(() => {
    if (!id) return;

    const load = async () => {
      try {
        setLoading(true);

        await Promise.all([
          loadClient(),
          loadBalance(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  // ================= OPEN EDIT =================

  const openEditModal = () => {
    if (!client) return;

    setForm({
      name: client.name || "",
      commission_rate:
        client.commission_rate !== null &&
        client.commission_rate !== undefined
          ? String(client.commission_rate)
          : "",
      comment: client.comment || "",
      telegram_chat_id:
        client.telegram_chat_id !== null &&
        client.telegram_chat_id !== undefined
          ? String(client.telegram_chat_id)
          : "",
      official_name: client.official_name || "",
      official_adress: client.official_adress || "",
      tax_identification_number:
        client.tax_identification_number || "",
      registration_number:
        client.registration_number || "",
      kpp: client.kpp || "",
      bank_account: client.bank_account || "",
      bank_nane: client.bank_nane || "",
      correspondent_account:
        client.correspondent_account || "",
      bik: client.bik || "",
      phone: client.phone || "",
      email: client.email || "",
      authorized_person: client.authorized_person || "",
    });

    setIsEditOpen(true);
  };

  // ================= CLOSE EDIT =================

  const closeEditModal = () => {
    if (saving) return;

    setIsEditOpen(false);
    setForm(emptyForm);
  };

  // ================= FORM CHANGE =================

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ================= PATCH CLIENT =================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      alert("Введите название клиента");
      return;
    }

    try {
      setSaving(true);

      const telegramRaw = String(form.telegram_chat_id || "").trim();
      const telegram_chat_id =
        telegramRaw === "" ? null : Number(telegramRaw);
      
      const payload = {
        name: form.name.trim(),
        commission_rate: form.commission_rate,
        comment: form.comment,
        telegram_chat_id,

        official_name: form.official_name,
        official_adress: form.official_adress,
        tax_identification_number:
          form.tax_identification_number,
        registration_number:
          form.registration_number,
        kpp: form.kpp,
        bank_account: form.bank_account,
        bank_nane: form.bank_nane,
        correspondent_account:
          form.correspondent_account,
        bik: form.bik,
        phone: form.phone,
        email: form.email,
        authorized_person: form.authorized_person,
      };

      const res = await authFetch(`${API_URL}${id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let errorMessage = "Ошибка сохранения клиента";

        try {
          const data = await res.json();

          if (data.detail) {
            errorMessage = data.detail;
          } else if (typeof data === "object") {
            errorMessage = Object.entries(data)
              .map(([field, errors]) => {
                const message = Array.isArray(errors)
                  ? errors.join(", ")
                  : String(errors);

                return `${field}: ${message}`;
              })
              .join("\n");
          }
        } catch {
          // Ответ не JSON
        }

        throw new Error(errorMessage);
      }

      const updatedClient = await res.json();

      setClient(updatedClient);

      setIsEditOpen(false);
      setForm(emptyForm);
    } catch (err) {
      console.error("Ошибка сохранения клиента:", err);

      alert(
        err.message || "Ошибка сохранения клиента"
      );
    } finally {
      setSaving(false);
    }
  };

  // ================= DELETE CLIENT =================

  const handleDeleteClient = async () => {
    try {
      setDeleting(true);

      const res = await authFetch(`${API_URL}${id}/`, {
        method: "DELETE",
      });

      if (!res.ok) {
        let errorMessage = "Ошибка удаления клиента";

        try {
          const data = await res.json();

          if (data.detail) {
            errorMessage = data.detail;
          }
        } catch {
          // DELETE может вернуть пустой ответ
        }

        throw new Error(errorMessage);
      }

      setShowDeleteConfirm(false);

      navigate("/clients");
    } catch (err) {
      console.error("Ошибка удаления клиента:", err);

      alert(
        err.message || "Ошибка удаления клиента"
      );
    } finally {
      setDeleting(false);
    }
  };

  // ================= LOADING =================

  if (loading) {
    return (
      <div className="p-6">
        {t("loading") || "Загрузка..."}
      </div>
    );
  }

  // ================= NOT FOUND =================

  if (!client) {
    return (
      <div className="p-6 space-y-4">
        <p>
          {t("client_not_found") ||
            "Клиент не найден"}
        </p>

        <Button onClick={() => navigate("/clients")}>
          {t("clients")}
        </Button>
      </div>
    );
  }

  // ================= UI =================

  return (
    <div className="space-y-6">

      {/* ================= HEADER ================= */}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

        <div>
          <h1 className="text-3xl font-bold">
            {t("client")} #{client.id}
          </h1>

          <p className="text-gray-500">
            {t("detail_info")}
          </p>
        </div>

        {/* ACTIONS */}

        <div className="flex flex-wrap gap-3">

          <Button
            variant="edit"
            onClick={openEditModal}
          >
            {t("edit")}
          </Button>

          <Button
            variant="danger"
            onClick={() =>
              setShowDeleteConfirm(true)
            }
          >
            {t("delete")}
          </Button>

        </div>
      </div>

      {/* ================= MAIN INFO ================= */}

      <div className="bg-white border rounded-2xl p-6 shadow-sm space-y-3">

        <h2 className="font-semibold text-lg mb-4">
          {t("detail_info")}
        </h2>

        <p>
          <b>{t("name")}:</b>{" "}
          {client.name || "-"}
        </p>

        <p>
          <b>{t("tracking_code")}:</b>{" "}
          {client.tracking_code || "-"}
        </p>

        <p>
          <b>{t("commission_rate")}:</b>{" "}
          {client.commission_rate || "-"}
        </p>


        <p>
          <b>Telegram chat ID:</b>{" "}
          {client.telegram_chat_id ?? "-"}
        </p>


        <p>
          <b>{t("comment")}:</b>{" "}
          {client.comment || "-"}
        </p>

      </div>

      {/* ================= BALANCE ================= */}

      <div
        onClick={() =>
          navigate(`/clients/${id}/balance`)
        }
        className="bg-white border rounded-2xl p-5 shadow-sm cursor-pointer hover:shadow-md transition"
      >

        <h2 className="text-lg font-semibold text-gray-700 mb-2">
          {t("client_balance")}
        </h2>

        <div className="flex items-end gap-2">

          <span className="text-3xl font-bold text-blue-600">
            ¥{balance?.balance ?? 0}
          </span>

          <span className="text-gray-500 mb-1">
            CNY
          </span>

        </div>

        <p className="text-sm text-gray-500 mt-2">
          {t("current_balance")}
        </p>

      </div>

      {/* ================= OFFICIAL INFORMATION ================= */}

      <div className="bg-white border rounded-2xl p-5 shadow-sm">

        <h2 className="font-semibold text-lg mb-4">
          Официальная информация
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              Юридическое название:
            </span>{" "}
            {client.official_name || "-"}
          </p>

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              Юридический адрес:
            </span>{" "}
            {client.official_adress || "-"}
          </p>

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              ИНН:
            </span>{" "}
            {client.tax_identification_number || "-"}
          </p>

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              ОГРН/ОГРНИП:
            </span>{" "}
            {client.registration_number || "-"}
          </p>

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              КПП:
            </span>{" "}
            {client.kpp || "-"}
          </p>

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              Р/с:
            </span>{" "}
            {client.bank_account || "-"}
          </p>

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              Название банка:
            </span>{" "}
            {client.bank_nane || "-"}
          </p>

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              К/с:
            </span>{" "}
            {client.correspondent_account || "-"}
          </p>

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              БИК:
            </span>{" "}
            {client.bik || "-"}
          </p>

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              Телефон:
            </span>{" "}
            {client.phone || "-"}
          </p>

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              E-mail:
            </span>{" "}
            {client.email || "-"}
          </p>

          <p className="text-gray-500">
            <span className="font-medium text-gray-700">
              Официальный представитель:
            </span>{" "}
            {client.authorized_person || "-"}
          </p>

        </div>
      </div>

      {/* ================= GRID ================= */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* SUPPLIES */}

        <div className="bg-white border rounded-2xl p-5 shadow-sm">

          <h2 className="font-semibold mb-2">
            {t("supplies")}
          </h2>

          <p className="text-gray-500">
            {t("no_data")}
          </p>

        </div>

        {/* PRODUCTS */}

        <div
          onClick={() =>
            navigate(`/clients/${id}/products`)
          }
          className="bg-white border rounded-2xl p-5 shadow-sm cursor-pointer hover:shadow-md transition"
        >

          <h2 className="font-semibold mb-2">
            {t("products")}
          </h2>

          <p className="text-gray-500">
            {t("go_to_products")}
          </p>

        </div>

        {/* WAREHOUSES */}

        <div className="bg-white border rounded-2xl p-5 shadow-sm">

          <h2 className="font-semibold mb-2">
            {t("warehouses")}
          </h2>

          <p className="text-gray-500">
            {t("no_data")}
          </p>

        </div>

        {/* HISTORY */}

        <div className="bg-white border rounded-2xl p-5 shadow-sm">

          <h2 className="font-semibold mb-2">
            {t("history")}
          </h2>

          <p className="text-gray-500">
            {t("no_data")}
          </p>

        </div>

        {/* STATISTICS */}

        <div className="bg-white border rounded-2xl p-5 shadow-sm md:col-span-2">

          <h2 className="font-semibold mb-2">
            {t("statistics")}
          </h2>

          <p className="text-gray-500">
            {t("no_data")}
          </p>

        </div>

        {/* COMMENT */}

        <div className="bg-white border rounded-2xl p-5 shadow-sm md:col-span-2">

          <h2 className="font-semibold mb-2">
            {t("comment")}
          </h2>

          <p className="text-gray-500">
            {client.comment ||
              t("no_comments")}
          </p>

        </div>

      </div>

      {/* ================= EDIT MODAL ================= */}

      <Modal
        isOpen={isEditOpen}
        onClose={closeEditModal}
        title={t("edit_client")}
      >

        <form
          onSubmit={handleSubmit}
          className="space-y-5"
        >

          {/* ================= BASIC ================= */}

          <div>
            <h3 className="font-semibold text-lg mb-3">
              Основная информация
            </h3>

            <div className="space-y-3">

              <input
                type="text"
                placeholder={t("name")}
                value={form.name}
                onChange={(e) =>
                  handleChange(
                    "name",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
                required
              />

              <input
                type="number"
                step="0.01"
                placeholder={t("commission_rate")}
                value={form.commission_rate}
                onChange={(e) =>
                  handleChange(
                    "commission_rate",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />


              <input
                type="text"
                inputMode="numeric"
                placeholder="Telegram chat ID (например -1001234567890)"
                value={form.telegram_chat_id}
                onChange={(e) =>
                  handleChange(
                    "telegram_chat_id",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />

              <textarea
                placeholder={t("comment")}
                value={form.comment}
                onChange={(e) =>
                  handleChange(
                    "comment",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
                rows={3}
              />

            </div>
          </div>

          {/* ================= OFFICIAL ================= */}

          <div>
            <h3 className="font-semibold text-lg mb-3">
              Официальная информация
            </h3>

            <div className="space-y-3">

              <input
                type="text"
                placeholder="Юридическое название"
                value={form.official_name}
                onChange={(e) =>
                  handleChange(
                    "official_name",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />

              <input
                type="text"
                placeholder="Юридический адрес"
                value={form.official_adress}
                onChange={(e) =>
                  handleChange(
                    "official_adress",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />

              <input
                type="text"
                placeholder="ИНН"
                value={
                  form.tax_identification_number
                }
                onChange={(e) =>
                  handleChange(
                    "tax_identification_number",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />

              <input
                type="text"
                placeholder="ОГРН/ОГРНИП"
                value={
                  form.registration_number
                }
                onChange={(e) =>
                  handleChange(
                    "registration_number",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />

              <input
                type="text"
                placeholder="КПП"
                value={form.kpp}
                onChange={(e) =>
                  handleChange(
                    "kpp",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />

              <input
                type="text"
                placeholder="Расчетный счет"
                value={form.bank_account}
                onChange={(e) =>
                  handleChange(
                    "bank_account",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />

              <input
                type="text"
                placeholder="Название банка"
                value={form.bank_nane}
                onChange={(e) =>
                  handleChange(
                    "bank_nane",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />

              <input
                type="text"
                placeholder="Корреспондентский счет"
                value={
                  form.correspondent_account
                }
                onChange={(e) =>
                  handleChange(
                    "correspondent_account",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />

              <input
                type="text"
                placeholder="БИК"
                value={form.bik}
                onChange={(e) =>
                  handleChange(
                    "bik",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />

              <input
                type="text"
                placeholder="Телефон"
                value={form.phone}
                onChange={(e) =>
                  handleChange(
                    "phone",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />

              <input
                type="email"
                placeholder="E-mail"
                value={form.email}
                onChange={(e) =>
                  handleChange(
                    "email",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />


              <input
                type="authorized_person"
                placeholder="Официальный представитель"
                value={form.authorized_person}
                onChange={(e) =>
                  handleChange(
                    "authorized_person",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border px-4 py-3"
              />


            </div>
          </div>

          {/* ================= SAVE ================= */}

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={saving}
          >
            {saving
              ? "Сохранение..."
              : t("save")}
          </Button>

        </form>

      </Modal>

      {/* ================= DELETE CONFIRM ================= */}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          if (!deleting) {
            setShowDeleteConfirm(false);
          }
        }}
        onConfirm={handleDeleteClient}
        title={t("delete_client")}
        description={
          <>
            {t("delete_confirm", {
              name: client.name,
            })}
            ?
          </>
        }
        confirmText={
          deleting
            ? "Удаление..."
            : t("delete")
        }
      />

    </div>
  );
}
